import { useContext, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import {
    Button,
    Card,
    InputNumber,
    Modal,
    Space,
    Typography,
    message,
} from 'antd'
import { ExclamationCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { AppContext } from '../../context/AppContext'
import { splitGiftCard } from '../../network/GiftCards'

const { Text, Title } = Typography

const toCents = (amount) => Math.round(Number(amount) * 100)
const fromCents = (cents) => Math.round(cents) / 100

export const buildEqualSplitAmounts = (totalAmount, parts) => {
    const totalCents = toCents(totalAmount)
    const base = Math.floor(totalCents / parts)
    const amounts = Array.from({ length: parts }, () => fromCents(base))
    const assigned = base * parts
    amounts[parts - 1] = fromCents(base + (totalCents - assigned))
    return amounts
}

const formatAmount = (amount) => fromCents(toCents(amount)).toFixed(2)

const SplitGiftCardSection = ({ giftCard }) => {
    const { t } = useTranslation()
    const { token } = useContext(AppContext)
    const history = useHistory()
    const queryClient = useQueryClient()

    const [expanded, setExpanded] = useState(false)
    const [mode, setMode] = useState('preset')
    const [presetParts, setPresetParts] = useState(2)
    const [customParts, setCustomParts] = useState(2)
    const [customAmounts, setCustomAmounts] = useState(() =>
        buildEqualSplitAmounts(giftCard.amount, 2)
    )
    const [submitting, setSubmitting] = useState(false)

    const amounts = useMemo(() => {
        if (mode === 'preset') {
            return buildEqualSplitAmounts(giftCard.amount, presetParts)
        }
        return customAmounts
    }, [mode, presetParts, customAmounts, giftCard.amount])

    const sumCents = amounts.reduce((sum, amount) => sum + toCents(amount), 0)
    const parentCents = toCents(giftCard.amount)
    const sumMatches = sumCents === parentCents
    const allPositive = amounts.every((amount) => Number(amount) > 0)
    const canSubmit = amounts.length >= 2 && sumMatches && allPositive && !submitting

    const previewIds = amounts.map(
        (_, index) =>
            `${giftCard.giftCardID}-${String(index + 1).padStart(3, '0')}`
    )

    const applyCustomParts = (parts) => {
        const nextParts = Math.max(2, Math.min(20, Number(parts) || 2))
        setCustomParts(nextParts)
        setCustomAmounts(buildEqualSplitAmounts(giftCard.amount, nextParts))
    }

    const updateCustomAmount = (index, value) => {
        setCustomAmounts((prev) => {
            const next = [...prev]
            next[index] = value == null ? 0 : value
            return next
        })
    }

    const runSplit = async () => {
        setSubmitting(true)
        try {
            const result = await splitGiftCard(
                giftCard.id || giftCard._id,
                amounts.map((amount) => fromCents(toCents(amount))),
                token.token
            )
            message.success(
                t('GIFT_CARDS_SPLIT_SUCCESS', {
                    count: result?.children?.length || amounts.length,
                })
            )
            queryClient.invalidateQueries('getGiftCards')
            queryClient.invalidateQueries('getSingleGiftCard')
            queryClient.invalidateQueries('getCustomerGiftCards')
            history.push('/gift-cards')
        } catch (err) {
            const apiMessage =
                err?.response?.data?.message || err?.message || err
            message.error(
                typeof apiMessage === 'string'
                    ? apiMessage
                    : t('GIFT_CARDS_SPLIT_ERROR')
            )
        } finally {
            setSubmitting(false)
        }
    }

    const confirmSplit = () => {
        Modal.confirm({
            title: t('GIFT_CARDS_SPLIT_CONFIRM_TITLE'),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>{t('GIFT_CARDS_SPLIT_CONFIRM_MESSAGE', {
                        id: giftCard.giftCardID,
                        amount: formatAmount(giftCard.amount),
                        count: amounts.length,
                    })}</p>
                    <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                        {amounts.map((amount, index) => (
                            <li key={previewIds[index]}>
                                {previewIds[index]} — ¥{formatAmount(amount)}
                            </li>
                        ))}
                    </ul>
                </div>
            ),
            okText: t('GIFT_CARDS_SPLIT_CONFIRM_OK'),
            cancelText: t('ROLES_CANCEL'),
            onOk: runSplit,
        })
    }

    return (
        <Card
            className="form-wrapper-card"
            style={{ height: 'fit-content', marginTop: 0 }}
            bodyStyle={expanded ? undefined : { display: 'none', padding: 0 }}
            title={
                <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded((open) => !open)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setExpanded((open) => !open)
                        }
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    <Title level={4} style={{ margin: 0 }}>
                        {t('GIFT_CARDS_SPLIT_TITLE')}
                    </Title>
                    {expanded ? <UpOutlined /> : <DownOutlined />}
                </div>
            }
        >
            <Text type="secondary">
                {t('GIFT_CARDS_SPLIT_DESCRIPTION', {
                    amount: formatAmount(giftCard.amount),
                })}
            </Text>

            <div style={{ marginTop: 16 }}>
                <Title level={5} style={{ marginBottom: 8 }}>
                    {t('GIFT_CARDS_SPLIT_PRESETS')}
                </Title>
                <Space wrap>
                    {[2, 3, 4, 5].map((parts) => (
                        <Button
                            key={parts}
                            type={
                                mode === 'preset' && presetParts === parts
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => {
                                setMode('preset')
                                setPresetParts(parts)
                            }}
                        >
                            {t('GIFT_CARDS_SPLIT_IN', { count: parts })}
                        </Button>
                    ))}
                    <Button
                        type={mode === 'custom' ? 'primary' : 'default'}
                        onClick={() => {
                            setMode('custom')
                            applyCustomParts(customParts)
                        }}
                    >
                        {t('GIFT_CARDS_SPLIT_CUSTOM')}
                    </Button>
                </Space>
            </div>

            {mode === 'custom' && (
                <div style={{ marginTop: 16 }}>
                    <Space align="center" style={{ marginBottom: 12 }}>
                        <Text>{t('GIFT_CARDS_SPLIT_PARTS')}</Text>
                        <InputNumber
                            min={2}
                            max={20}
                            value={customParts}
                            onChange={applyCustomParts}
                        />
                    </Space>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {customAmounts.map((amount, index) => (
                            <Space key={`part-${index}`}>
                                <Text style={{ width: 140 }}>
                                    {previewIds[index]}
                                </Text>
                                <InputNumber
                                    min={0.01}
                                    step={0.01}
                                    precision={2}
                                    value={amount}
                                    onChange={(value) =>
                                        updateCustomAmount(index, value)
                                    }
                                    formatter={(value) => `¥ ${value}`}
                                    parser={(value) =>
                                        value.replace(/¥\s?/g, '')
                                    }
                                />
                            </Space>
                        ))}
                    </Space>
                </div>
            )}

            <div style={{ marginTop: 16 }}>
                <Text>
                    {t('GIFT_CARDS_SPLIT_PREVIEW_SUM', {
                        sum: formatAmount(fromCents(sumCents)),
                        total: formatAmount(giftCard.amount),
                    })}
                </Text>
                {!sumMatches && (
                    <div>
                        <Text type="danger">
                            {t('GIFT_CARDS_SPLIT_SUM_MISMATCH')}
                        </Text>
                    </div>
                )}
                {mode === 'preset' && (
                    <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                        {amounts.map((amount, index) => (
                            <li key={previewIds[index]}>
                                {previewIds[index]} — ¥{formatAmount(amount)}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <Button
                type="primary"
                danger
                htmlType="button"
                style={{ marginTop: 16 }}
                disabled={!canSubmit}
                loading={submitting}
                onClick={(e) => {
                    e.preventDefault()
                    confirmSplit()
                }}
            >
                {t('GIFT_CARDS_SPLIT_SUBMIT')}
            </Button>
        </Card>
    )
}

export default SplitGiftCardSection
